import type { EntityType } from '@repo/common/enums/entityType';
import axios from 'axios';
import { isNil, isPlainObject } from 'lodash-es';
import { api } from '../../../network';
import { authHeaders, getBaseUrl } from '../../api/common';
import { BackgroundJob } from './backgroundJob';

const filterNil = (obj: object) =>
	Object.fromEntries(Object.entries(obj).filter(([, value]) => !isNil(value)));

export function convertObjectToUrlSearchParams(options: object) {
	const updateOptions = {
		...Object.fromEntries(
			Object.entries(filterNil(options)).map(([key, value]) => {
				if (
					(key === 'native_type' || key === 'native_type__exclude') &&
					Array.isArray(value)
				) {
					return [key, JSON.stringify(value)];
				}
				return [
					key,
					isPlainObject(value) ? JSON.stringify(filterNil(value)) : value,
				];
			})
		),
	};
	return new URLSearchParams(updateOptions);
}

export const search = async (options: object, highlight?: boolean) => {
	const updateOptions = convertObjectToUrlSearchParams({
		highlight: !isNil(highlight) ? String(highlight) : undefined,
		...options,
	});
	const res = await axios.get(`${api()}search/?${updateOptions.toString()}`);
	return res;
};

export const getSearchFilters = async (): Promise<
	Record<
		'databases' | 'groups' | 'schemas' | 'native_types',
		{
			label: string;
			value: string;
			entityType?: EntityType;
		}[]
	>
> => {
	const resp = await axios.get(`${api()}search/filters/`, authHeaders());
	return resp.data;
};
export const publishAll = async () =>
	axios.post(`${api()}resource/all/publish_all/`, {}, authHeaders());

export const exportData = async (type: string) => {
	const response = await axios.get(`${api()}metadata/data/`, {
		...authHeaders(),
		params: {
			type,
		},
	});
	return new BackgroundJob(response.data);
};

export const saveBlob = (blob: Blob, filename: string) => {
	const link = document.createElement('a');
	const urlObject = URL.createObjectURL(blob);

	link.href = urlObject;
	link.download = filename;
	document.body.appendChild(link);
	link.click();

	document.body.removeChild(link);
	URL.revokeObjectURL(urlObject);
};

export const downloadFile = async (path: string, fileName = 'export.csv') => {
	const fullPath = path.startsWith('http') ? path : getBaseUrl() + path;
	const response = await axios.get(fullPath);
	saveBlob(new Blob([response.data], { type: 'text/csv' }), fileName);
};

export const downloadImportTemplate = (
	customProperties?: Array<{ name: string }>
): void => {
	// Create CSV header with base columns and custom property columns
	const baseColumns = [
		'id',
		'title',
		'entity_type',
		'description',
		'definition',
		'sql',
		'pii',
		'verified',
		'published',
		'collections',
		'owners',
		'tags',
		'teams',
		'synonyms',
	];
	const customPropertyColumns = customProperties?.map((cp) => cp.name) || [];
	const headers = [...baseColumns, ...customPropertyColumns];

	// Create example rows similar to the backend template format
	const exampleRow1 = [
		'<existing_id_in_Secoda_or_empty>',
		'<string_or_empty>',
		'<glossary_or_document>',
		'<string_or_empty>',
		'<string_or_empty>',
		'<string_or_empty> <only_applicable_to_glossary_entity_type>',
		'<FALSE_or_TRUE> <default_FALSE_when_empty>',
		'<FALSE_or_TRUE>  <default_False_when_empty>',
		'<False_or_True> <default_False_when_empty>',
		'<comma_separated_list_of_collection_name>',
		'<comma_separated_list_of_email_or_user_group_name>',
		'<comma_separated_list_of_tag_name>',
		'<comma_separated_list_of_team_name> <default_to_general_team>',
		'<comma_separated_list_of_synonyms>',
		...customPropertyColumns.map(() => '<value>'),
	];

	const exampleRow2 = [
		'',
		'New Glossary',
		'glossary',
		'This will be a new glossary created in your workspace',
		'',
		'SELECT * FROM customers LIMIT 100;',
		'FALSE',
		'TRUE',
		'TRUE',
		'Collection 1; Collection 2',
		'owner@secoda.co;Engineering Team',
		'Tag 1;Tag 2',
		'',
		'',
		...customPropertyColumns.map(() => '<value>'),
	];

	// Create CSV content (header row + description row + example rows)
	// Row 1: Description/instructions, Row 2: New resource example, Row 3: Existing resource example
	const csvContent = [
		headers.join(','),
		exampleRow1.join(','),
		exampleRow2.join(','),
	].join('\n');

	const blob = new Blob([csvContent], { type: 'text/csv' });
	saveBlob(blob, 'import.csv');
};

export const downloadCustomPropertiesTemplate = (
	customProperties: Array<{ name: string }>
) => {
	if (!customProperties || customProperties.length === 0) {
		// If no custom properties, create a template with just id
		const csvContent = ['id', '<existing_id_in_Secoda>'].join('\n');
		const blob = new Blob([csvContent], { type: 'text/csv' });
		saveBlob(blob, 'custom_properties_import.csv');
		return;
	}

	// Create CSV header with id and custom property names
	const headers = ['id', ...customProperties.map((cp) => cp.name)];

	// Create CSV content (header row + example row)
	const csvContent = [
		headers.join(','),
		`<existing_id_in_Secoda>,${customProperties.map(() => '<value>').join(',')}`,
	].join('\n');

	const blob = new Blob([csvContent], { type: 'text/csv' });
	saveBlob(blob, 'custom_properties_import.csv');
};

export const downloadExportTemplate = () =>
	downloadFile('metadata/data/template_export/', 'export.csv');
